import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { LoadingButton, RepliconDialog } from '~/modules/common/components';
import { RoleAssignmentMode } from '~/types';
import TaskRolesList from './TaskRolesList';
import UserProfileRolesList from './UserProfileRolesList';
import SpecificRolesSelector from './SpecificRolesSelector';

const useStyles = makeStyles(theme => ({
  content: {
    paddingBottom: theme.spacing(1)
  },
  skeleton: {
    margin: theme.spacing(1, 0)
  },
  radioOption: {
    alignItems: 'flex-start',
    marginLeft: 0,
    marginBottom: theme.spacing(0.5)
  },
  radioControl: {
    paddingTop: theme.spacing(0.5)
  },
  optionSubtitle: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    display: 'block'
  },
  selectedOptionContent: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(4)
  },
  userProfileMessage: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary
  },
  actions: {
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(2)
  }
}));

const radioOptions = [
  {
    value: RoleAssignmentMode.Inherit,
    labelId: 'selectTaskRolesDialog.taskRoles',
    subtitleId: 'selectTaskRolesDialog.taskRolesSubtitle'
  },
  {
    value: RoleAssignmentMode.UserProfileRoles,
    labelId: 'selectTaskRolesDialog.userProfileRoles',
    subtitleId: 'selectTaskRolesDialog.userProfileRolesSubtitle'
  },
  {
    value: RoleAssignmentMode.SpecificRoles,
    labelId: 'selectTaskRolesDialog.specificRoles',
    subtitleId: 'selectTaskRolesDialog.specificRolesSubtitle'
  }
];

const RoleAssignmentDialog = ({
  open,
  onClose,
  onSave,
  taskUri,
  teamMemberUserId
}) => {
  const classes = useStyles();

  const [roleAssignmentMode, setRoleAssignmentMode] = useState(
    RoleAssignmentMode.Inherit
  );
  const [specificRoles, setSpecificRoles] = useState([]);

  const handleModeChange = useCallback(
    e => setRoleAssignmentMode(e.target.value),
    []
  );

  const getRadioButtonLabel = useCallback(
    ({ labelId, subtitleId }) => (
      <span>
        <FormattedMessage id={labelId} />
        <span className={classes.optionSubtitle}>
          <FormattedMessage id={subtitleId} />
        </span>
      </span>
    ),
    [classes.optionSubtitle]
  );

  const handleSave = useCallback(async () => {
    await onSave({ roleAssignmentMode, specificRoles });

    onClose();
  }, [roleAssignmentMode, specificRoles, onSave, onClose]);

  return (
    <RepliconDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      enableUnsavedChangesWarning
    >
      <DialogTitle>
        <FormattedMessage id="selectTaskRolesDialog.title" />
      </DialogTitle>
      <DialogContent className={classes.content}>
        <>
          <RadioGroup value={roleAssignmentMode} onChange={handleModeChange}>
            {radioOptions.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                className={classes.radioOption}
                control={
                  <Radio
                    color="primary"
                    size="small"
                    className={classes.radioControl}
                  />
                }
                label={getRadioButtonLabel(option)}
              />
            ))}
          </RadioGroup>

          <div className={classes.selectedOptionContent}>
            {roleAssignmentMode === RoleAssignmentMode.Inherit && (
              <TaskRolesList taskUri={taskUri} />
            )}
            {roleAssignmentMode === RoleAssignmentMode.UserProfileRoles && (
              <>
                <Typography className={classes.userProfileMessage}>
                  <FormattedMessage id="selectTaskRolesDialog.userProfileRolesMessage" />
                </Typography>
                {teamMemberUserId && (
                  <UserProfileRolesList userId={teamMemberUserId} />
                )}
              </>
            )}
            {roleAssignmentMode === RoleAssignmentMode.SpecificRoles && (
              <SpecificRolesSelector
                selectedRoles={specificRoles}
                onSelectedRolesChange={setSpecificRoles}
              />
            )}
          </div>
        </>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} variant="contained">
          <FormattedMessage id="button.cancel" />
        </Button>
        <LoadingButton onClick={handleSave} color="primary" variant="contained">
          <FormattedMessage id="button.save" />
        </LoadingButton>
      </DialogActions>
    </RepliconDialog>
  );
};

RoleAssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  taskUri: PropTypes.string.isRequired,
  teamMemberUserId: PropTypes.string
};

export default RoleAssignmentDialog;
